import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Async from 'components/Async';
import ReportEmptyData from 'modules/Report/components/EmptyData';
import ReportInfoBlock from 'modules/Report/components/InfoBlock';
import ReportInfoBlockList from 'modules/Report/components/InfoBlock/List';
import ReportInfoBlockListItem from 'modules/Report/components/InfoBlock/List/Item';
import ReportInfoBlockGrid from 'modules/Report/components/InfoBlock/grid';
import store from 'store';

import RBAC from '@common/components/RBAC';
import { useServiceRouter } from '@common/components/ServiceRouter';
import { getRoute } from '@common/constants/CampusRoutes';
import { getPluralKey } from '@common/helpers/getPluralKey';
import useModel from '@common/helpers/useModel';
import Documents from '@common/models/Documents/Documents';
import { IDocumentsItemModel } from '@common/models/Documents/DocumentsItem';
import { IGroupModel } from '@common/models/Groups/Group';
import { IReportModel } from '@common/models/Reports/Report';
import ReportsModel, { IReportsModel } from '@common/models/Reports/Reports';
import { IStudentModel } from '@common/models/Users/Students/Student';

function AchievementPage() {
	const { t } = useTranslation();
	const { checkService } = useServiceRouter();

	const isAcademy = checkService('academy');
	const getAcademyReportSubtitle = (report: IReportModel) =>
		report.dateTime.isValid ? report.dateTime.toFormat('dd.MM.yyyy') : '';
	const getPublishedAtMillis = (report: IReportModel) =>
		report.dateTime.isValid ? report.dateTime.toMillis() : Number.NEGATIVE_INFINITY;
	const compareByPublishedAtDesc = (a: IReportModel, b: IReportModel) => {
		const aMillis = getPublishedAtMillis(a);
		const bMillis = getPublishedAtMillis(b);
		if (aMillis === bMillis) return 0;
		return bMillis - aMillis;
	};
	const isAcademyParent = store.profile.checkRole('Parent') && store.services.current.id === 1;

	const academicReports: IReportsModel = useModel(
		ReportsModel,
		{
			student_id: store.currentStudentId,
			service_id: store.services.current.id,
			folder: 'academic',
		},
		[store.currentStudentId, store.services.current.id],
	);

	const mentorReports: IReportsModel = useModel(
		ReportsModel,
		{
			student_id: store.currentStudentId,
			service_id: store.services.current.id,
			folder: 'mentor',
		},
		[store.currentStudentId, store.services.current.id],
	);

	const groupIds = store.profile.parent?.students
		.flatMap((student: IStudentModel) => student.groups.map((group: IGroupModel) => group.id))
		.join(',');

	const currentService = store.services.current.id;

	const documents = useModel(Documents, { group_ids: groupIds, service_id: currentService }, [
		store.currentStudentId,
	]);

	return (
		<div>
			<ReportInfoBlockGrid>
				<ReportInfoBlock title={t('report.achievements.academicAchievements')}>
					<Async loading={academicReports.loading}>
						{academicReports.data.length ? (
							academicReports.data
								.filter((report: IReportModel) => report.is_published)
								.sort(compareByPublishedAtDesc)
								.map((report: IReportModel, index: number) => (
									<ReportInfoBlockListItem
										key={index}
										type="download"
										title={report?.type as string}
										subTitle={
											isAcademy
												? getAcademyReportSubtitle(report)
												: `${report?.academic_semester_name} - ${report?.dateTime.year}`
										}
										download={`${report?.subtype} ${report?.academic_semester_name} ${report?.lang}.pdf`}
										href={report.pdf_url as string}
									/>
								))
						) : (
							<ReportEmptyData type="leaf" />
						)}
					</Async>
				</ReportInfoBlock>
				{!isAcademyParent && (
					<>
						<ReportInfoBlock title={t('report.achievements.mentorSupport')}>
							<Async loading={mentorReports.loading}>
								{mentorReports.data.length ? (
									mentorReports.data
										.filter((report: IReportModel) => report.is_published)
										.map((report: IReportModel, index: number) => (
											<ReportInfoBlockListItem
												key={index}
												type="download"
												title={report?.type}
												subTitle={`${report?.academic_semester_name} - ${report?.dateTime.year}`}
												download={`${report?.subtype} ${report?.academic_semester_name} ${report?.lang}.pdf`}
												href={report.pdf_url}
											/>
										))
								) : (
									<ReportEmptyData type="document" />
								)}
							</Async>
						</ReportInfoBlock>
						<ReportInfoBlock
							title={t('report.achievements.myEducationPlannerPortfolio')}
							toolTipText={t('report.achievements.tooltip')}
							href={getRoute(['REPORT', 'ACHIEVEMENT', 'PORTFOLIO'])}
						>
							<ReportInfoBlockList>
								<ReportInfoBlockListItem
									type="info"
									title={t('report.achievements.projects')}
									subTitle={t(`report.achievements.files.${getPluralKey(0)}`, { count: 0 })}
								/>
								<ReportInfoBlockListItem
									type="info"
									title={t('report.achievements.main')}
									subTitle={t(`report.achievements.files.${getPluralKey(0)}`, { count: 0 })}
								/>
								<ReportInfoBlockListItem
									type="info"
									title={t('report.achievements.knowledge')}
									subTitle={t(`report.achievements.files.${getPluralKey(0)}`, { count: 0 })}
								/>
							</ReportInfoBlockList>
						</ReportInfoBlock>
					</>
				)}
				{!!documents.data.length && (
					<RBAC granted={['Parent']}>
						<ReportInfoBlock
							title={t('report.achievements.documents.title')}
							// onAddFileClick={() => console.info('add file')}
						>
							{documents.data?.map((document: IDocumentsItemModel) => (
								<ReportInfoBlockListItem
									key={document.id}
									type="download"
									title={document.title}
									subTitle={t('report.achievements.documents.subTitle')}
									download={document.file_url}
									href={document.file_url}
								/>
							))}
						</ReportInfoBlock>
					</RBAC>
				)}
			</ReportInfoBlockGrid>
		</div>
	);
}

export default observer(AchievementPage);
